/**
 * School Management Component
 * 
 * Allows admins and superadmins to:
 * - Create new schools
 * - View all schools
 * - Update school information
 * - Assign principals to schools
 * 
 * Access: Admin/Superadmin only
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  createSchool,
  decideSchoolApproval,
  updateSchool,
  getAllSchools,
} from '../../services/schoolManagementService';
import { normalizeSchoolApprovalStatus, type SchoolDecision } from '../../services/schoolApprovalPolicy';
import type { School } from '../../types/lms';
import { FaPlus, FaSchool, FaEdit, FaUsers, FaMapMarkerAlt, FaPhone, FaGlobe, FaGraduationCap, FaBuilding, FaCalendarAlt } from 'react-icons/fa';
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../Components/ui/button';
import { Card, CardContent } from '../../Components/ui/card';
import { Input } from '../../Components/ui/input';
import { Label } from '../../Components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../Components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select';

const SchoolManagement = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [schools, setSchools] = useState<School[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [processingSchoolId, setProcessingSchoolId] = useState<string | null>(null);
  const [newSchoolData, setNewSchoolData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contactPerson: '',
    contactPhone: '',
    website: '',
    boardAffiliation: '',
    establishedYear: '',
    schoolType: '',
  });

  useEffect(() => {
    if (!profile) return;

    // Only admin/superadmin can access
    if (profile.role !== 'admin' && profile.role !== 'superadmin') {
      setLoading(false);
      return;
    }

    // Load schools
    const loadSchools = async () => {
      const schoolsData = await getAllSchools(profile);
      setSchools(schoolsData);
      setLoading(false);
    };

    loadSchools();

    // Load all users (for assigning principals)
    const usersQuery = query(collection(db, 'users'));
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    });

    return () => {
      unsubscribeUsers();
    };
  }, [profile]);

  const handleCreateSchool = async () => {
    if (!profile) return;

    if (!newSchoolData.name.trim()) {
      toast.error('School name is required');
      return;
    }

    const schoolId = await createSchool(profile, newSchoolData);

    if (schoolId) {
      setShowCreateModal(false);
      setNewSchoolData({
        name: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        contactPerson: '',
        contactPhone: '',
        website: '',
        boardAffiliation: '',
        establishedYear: '',
        schoolType: '',
      });
    }
  };

  const handleUpdateSchool = async () => {
    if (!profile || !selectedSchool) return;

    const success = await updateSchool(profile, selectedSchool.id, newSchoolData);
    if (success) {
      setShowEditModal(false);
      setSelectedSchool(null);
    }
  };

  const handleAssignPrincipal = async (schoolId: string, principalId: string) => {
    if (!profile) return;

    try {
      // Update school with principal_id
      await updateDoc(doc(db, 'schools', schoolId), {
        principal_id: principalId,
        updatedAt: serverTimestamp(),
      });

      // Update user with managed_school_id and set role to principal
      await updateDoc(doc(db, 'users', principalId), {
        managed_school_id: schoolId,
        school_id: schoolId, // Also set school_id for consistency
        role: 'principal',
        updatedAt: serverTimestamp(),
      });

      toast.success('Principal assigned to school successfully');
    } catch (error: any) {
      console.error('Error assigning principal:', error);
      toast.error(`Failed to assign principal: ${error.message}`);
    }
  };

  const handleSchoolDecision = async (school: School, decision: SchoolDecision) => {
    if (!profile) return;
    setProcessingSchoolId(school.id);
    try {
      const result = await decideSchoolApproval(profile, school, decision);
      setSchools((currentSchools) => currentSchools.map((currentSchool) => (
        currentSchool.id === school.id
          ? {
              ...currentSchool,
              approvalStatus: decision,
              schoolCode: result.schoolCode || currentSchool.schoolCode,
            }
          : currentSchool
      )));
      if (decision === 'approved') {
        toast.success(result.schoolCode
          ? `School approved. Teacher join code: ${result.schoolCode}`
          : 'School approved successfully');
      } else {
        toast.success('School registration rejected');
      }
    } catch (error: any) {
      console.error('Error deciding school registration:', error);
      toast.error(error?.message || 'Failed to update school approval');
    } finally {
      setProcessingSchoolId(null);
    }
  };

  const openEditModal = (school: School) => {
    setSelectedSchool(school);
    setNewSchoolData({
      name: school.name || '',
      address: school.address || '',
      city: school.city || '',
      state: school.state || '',
      pincode: school.pincode || '',
      contactPerson: school.contactPerson || '',
      contactPhone: school.contactPhone || '',
      website: school.website || '',
      boardAffiliation: school.boardAffiliation || '',
      establishedYear: school.establishedYear || '',
      schoolType: school.schoolType || '',
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading school management...</p>
        </div>
      </div>
    );
  }

  if (profile?.role !== 'admin' && profile?.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <Card className="max-w-md border-border">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Access denied. Admin or Superadmin role required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const principals = users.filter(u => u.role === 'principal' || u.role === 'teacher');

  return (
    <div className="min-h-screen bg-background pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                <FaSchool className="text-primary" />
              </div>
              School Management
            </h1>
            <p className="text-muted-foreground">Create and manage schools, assign principals</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.role === 'superadmin' && (
              <Button variant="outline" onClick={() => navigate('/admin/approvals')} className="gap-2">
                <ShieldCheck className="h-4 w-4" />
                School & User Approvals
              </Button>
            )}
            <Button onClick={() => setShowCreateModal(true)} className="gap-2">
              <FaPlus className="w-4 h-4" />
              Create School
            </Button>
          </div>
        </div>

        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <h2 className="font-semibold text-foreground">Approval hierarchy</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Superadmin approves schools. A school administrator or principal approves teachers for that school.
                  Teachers approve students requesting one of their classes; school leadership can also review students across the school.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schools List */}
        <div className="space-y-4">
          {schools.length === 0 ? (
            <Card className="border-border rounded-2xl">
              <CardContent className="p-8 text-center">
                <FaSchool className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No schools created yet</p>
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  Create Your First School
                </Button>
              </CardContent>
            </Card>
          ) : (
            schools.map((school) => {
              const approvalStatus = normalizeSchoolApprovalStatus(school.approvalStatus);
              const isPending = approvalStatus === 'pending';
              const isProcessing = processingSchoolId === school.id;
              const schoolPrincipal = users.find(u => 
                (u.managed_school_id === school.id) || (school.principal_id === u.uid)
              );
              const schoolStudents = users.filter(u => u.school_id === school.id && u.role === 'student');
              const schoolTeachers = users.filter(u => u.school_id === school.id && u.role === 'teacher');

              return (
                <Card key={school.id} className="rounded-xl border-border bg-card">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground mb-2 flex flex-wrap items-center gap-2">
                          <FaSchool className="text-primary" />
                          {school.name}
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                              approvalStatus === 'approved'
                                ? 'bg-emerald-500/15 text-emerald-600'
                                : approvalStatus === 'rejected'
                                  ? 'bg-red-500/15 text-red-600'
                                  : 'bg-amber-500/15 text-amber-700'
                            }`}
                          >
                            {approvalStatus}
                          </span>
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                          {school.address && (
                            <div className="flex items-center gap-2">
                              <FaMapMarkerAlt className="text-primary shrink-0" />
                              <span>{school.address}, {school.city}, {school.state} {school.pincode}</span>
                            </div>
                          )}
                          {school.contactPhone && (
                            <div className="flex items-center gap-2">
                              <FaPhone className="text-primary shrink-0" />
                              <span>{school.contactPhone}</span>
                            </div>
                          )}
                          {school.website && (
                            <div className="flex items-center gap-2">
                              <FaGlobe className="text-primary shrink-0" />
                              <a href={school.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                                {school.website}
                              </a>
                            </div>
                          )}
                          {school.boardAffiliation && (
                            <div className="flex items-center gap-2">
                              <FaGraduationCap className="text-primary shrink-0" />
                              <span>{school.boardAffiliation}</span>
                            </div>
                          )}
                          {school.establishedYear && (
                            <div className="flex items-center gap-2">
                              <FaCalendarAlt className="text-primary shrink-0" />
                              <span>Est. {school.establishedYear}</span>
                            </div>
                          )}
                          {school.schoolType && (
                            <div className="flex items-center gap-2">
                              <FaBuilding className="text-primary shrink-0" />
                              <span>{school.schoolType}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditModal(school)} className="gap-2">
                          <FaEdit />
                          Edit
                        </Button>
                        {isPending && profile.role === 'superadmin' && (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => handleSchoolDecision(school, 'rejected')}
                              className="gap-2 border-red-500/40 text-red-600 hover:bg-red-500/10"
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              disabled={isProcessing}
                              onClick={() => handleSchoolDecision(school, 'approved')}
                              className="gap-2"
                            >
                              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                              Approve school
                            </Button>
                          </div>
                        )}
                        {isPending && profile.role === 'admin' && (
                          <p className="max-w-48 text-right text-xs text-amber-700">
                            Superadmin approval required
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaUsers className="text-primary" />
                          <span className="text-foreground text-sm font-medium">Principal</span>
                        </div>
                        {schoolPrincipal ? (
                          <p className="text-muted-foreground text-sm">{schoolPrincipal.name || schoolPrincipal.displayName || schoolPrincipal.email}</p>
                        ) : (
                          <Select
                            onValueChange={(value) => value && handleAssignPrincipal(school.id, value)}
                          >
                            <SelectTrigger className="w-full bg-background border-border text-foreground">
                              <SelectValue placeholder="Assign Principal" />
                            </SelectTrigger>
                            <SelectContent>
                              {principals.map(p => (
                                <SelectItem key={p.uid} value={p.uid}>
                                  {p.name || p.displayName || p.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaUsers className="text-primary" />
                          <span className="text-foreground text-sm font-medium">Teachers ({schoolTeachers.length})</span>
                        </div>
                        <p className="text-muted-foreground text-sm">{schoolTeachers.length} teachers</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FaUsers className="text-primary" />
                          <span className="text-foreground text-sm font-medium">Students ({schoolStudents.length})</span>
                        </div>
                        <p className="text-muted-foreground text-sm">{schoolStudents.length} students</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create School Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">Create New School</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label className="text-foreground">School Name *</Label>
                <Input
                  value={newSchoolData.name}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, name: e.target.value })}
                  placeholder="Enter school name"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label className="text-foreground">Address</Label>
                <Input
                  value={newSchoolData.address}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, address: e.target.value })}
                  placeholder="Street address"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">City</Label>
                <Input
                  value={newSchoolData.city}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, city: e.target.value })}
                  placeholder="City"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">State</Label>
                <Input
                  value={newSchoolData.state}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, state: e.target.value })}
                  placeholder="State"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Pincode</Label>
                <Input
                  value={newSchoolData.pincode}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, pincode: e.target.value })}
                  placeholder="Pincode"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Contact Person</Label>
                <Input
                  value={newSchoolData.contactPerson}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, contactPerson: e.target.value })}
                  placeholder="Contact person name"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Contact Phone</Label>
                <Input
                  value={newSchoolData.contactPhone}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, contactPhone: e.target.value })}
                  placeholder="Phone number"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Website</Label>
                <Input
                  type="url"
                  value={newSchoolData.website}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, website: e.target.value })}
                  placeholder="https://example.com"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Board Affiliation</Label>
                <Input
                  value={newSchoolData.boardAffiliation}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, boardAffiliation: e.target.value })}
                  placeholder="e.g., CBSE, RBSE, ICSE"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Established Year</Label>
                <Input
                  value={newSchoolData.establishedYear}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, establishedYear: e.target.value })}
                  placeholder="Year"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">School Type</Label>
                <Input
                  value={newSchoolData.schoolType}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, schoolType: e.target.value })}
                  placeholder="e.g., Private, Public, International"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleCreateSchool}>
                Create School
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit School Modal */}
        <Dialog open={showEditModal && !!selectedSchool} onOpenChange={(open) => { if (!open) { setShowEditModal(false); setSelectedSchool(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">Edit School: {selectedSchool?.name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label className="text-foreground">School Name *</Label>
                <Input
                  value={newSchoolData.name}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, name: e.target.value })}
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label className="text-foreground">Address</Label>
                <Input
                  value={newSchoolData.address}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, address: e.target.value })}
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">City</Label>
                <Input
                  value={newSchoolData.city}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, city: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">State</Label>
                <Input
                  value={newSchoolData.state}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, state: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Pincode</Label>
                <Input
                  value={newSchoolData.pincode}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, pincode: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Contact Person</Label>
                <Input
                  value={newSchoolData.contactPerson}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, contactPerson: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Contact Phone</Label>
                <Input
                  value={newSchoolData.contactPhone}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, contactPhone: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Website</Label>
                <Input
                  type="url"
                  value={newSchoolData.website}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, website: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Board Affiliation</Label>
                <Input
                  value={newSchoolData.boardAffiliation}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, boardAffiliation: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Established Year</Label>
                <Input
                  value={newSchoolData.establishedYear}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, establishedYear: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">School Type</Label>
                <Input
                  value={newSchoolData.schoolType}
                  onChange={(e) => setNewSchoolData({ ...newSchoolData, schoolType: e.target.value })}
                  className="bg-background border-border text-foreground"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => { setShowEditModal(false); setSelectedSchool(null); }}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleUpdateSchool}>
                Update School
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SchoolManagement;
